import { useEffect, useState } from 'react';
import './app/shared/utils/yupMethods';
import './styles/global.css';
import './config/ApiConfig';
import { createRoot } from 'react-dom/client';
import { MsalProvider, useMsal } from '@azure/msal-react';
import axios from 'axios';
import { AuthenticationResult, CacheLookupPolicy, EventMessage, EventType, InteractionRequiredAuthError, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';
import { IconButton, Slide, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { Provider, useDispatch } from 'react-redux';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { loginRequest, msalInstance } from '@config/authConfig';
import { store } from '@store/store';
import { selectViewAsUserId, viewAsUserIdAction } from '@store/reducers/headerSlice';
import { muiTheme } from '@styles/muiTheme';
import { generateBrowserRouter } from '@config/routesConfig';
import { BASE_NAME } from '@shared/constants/constants';
import { EMenuItemType, IMenuItem } from '@shared/components/Layout/components/Sidebar/types';
import { SERVICES } from '@shared/constants/apiConfig';
import { withQueryParams } from '@shared/commonFunctions';
import { ILendingLoader } from '@shared/components/IlendingLoader';
import { getError } from '@shared/utils/helpers';
import { StyledCustomAlert } from '@styles/Layout/PopOversStyles';
import { useAppSelector } from '@store/hooks';
import { reactPlugin } from '@config/AppInsight';
import { selectMenuItemRefresh, stateMenuItemRefresh } from '@store/reducers/layoutSlice';
import { App } from './app/App';

const defaultPermission: IMenuItem = {
  id: 'default',
  text: 'No Permissions',
  menuItemType: EMenuItemType.Header,
  childMenuItems: [],
  link: '/no-permission',
  alertCount: 0,
  showAlert: false,
  highlightColor1: '',
  highlightColor2: '',
  order: 999,
  queueId: '',
  queueType: '',
  queueCount: 0,
  elementKey: 'NoPermission',
};

const CustomAlert = ({
  alertText,
  setOpenAlert
}: {
  alertText: string,
  setOpenAlert: React.Dispatch<React.SetStateAction<string>>
}) => {
  return <Slide direction="left" in={!!alertText}>
    <StyledCustomAlert
      variant="filled"
      severity="error"
      action={
        <IconButton
          color="inherit"
          size="small"
          onClick={() => setOpenAlert('')}
        >
          <Clear />
        </IconButton>
      }
    >
      {alertText}
    </StyledCustomAlert>
  </Slide>;
};

const Main = ({ pca }: { pca: IPublicClientApplication }) => (
  <AppInsightsContext.Provider value={reactPlugin}>
    <MsalProvider instance={pca}>
      <MuiThemeProvider theme={muiTheme}>
        <Provider store={store}>
          <MainApp />
        </Provider>
      </MuiThemeProvider>
    </MsalProvider>
  </AppInsightsContext.Provider>
);


const MainApp = () => {
  const dispatch = useDispatch();
  const viewAs = useAppSelector(selectViewAsUserId);
  const menuItemRefresh = useAppSelector(selectMenuItemRefresh);
  const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);
  const [openAlert, setOpenAlert] = useState('');
  
  const { instance, accounts, inProgress } = useMsal();
  const [loading, setLoading] = useState(false);

  const getData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<IMenuItem[]>(
        withQueryParams(
          `/${SERVICES.SEARCH}/search/menuitems`,
          viewAs.userId && {
            viewAsUserId: viewAs.userId,
          },
          false
        )
      );

      setMenuItems(data.length ? data : [defaultPermission]);
    } catch (err) {
      setMenuItems([{...defaultPermission, text: 'Error loading menu'}]);
      setOpenAlert(getError(err));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!loading && inProgress === InteractionStatus.None && accounts.length > 0) {
        if (menuItems.length > 0) {
          // Skip data refresh if already set - Refresh only in case of a menu is update fro menu editor or user has selected the user from view as user dropdown
          if (menuItemRefresh || viewAs.refreshMenu) {
            getData();
            menuItemRefresh && dispatch(stateMenuItemRefresh(false));
            viewAs.refreshMenu && dispatch(viewAsUserIdAction({...viewAs, refreshMenu: false}));
          }
            return;
        }

        const tokenRequest = {
          account: accounts[0],
          scopes: loginRequest.scopes,
          forceRefresh: false,
          cacheLookupPolicy: CacheLookupPolicy.Default, // will default to CacheLookupPolicy.Default if omitted
        };

        // Acquire an access token
        instance.acquireTokenSilent(tokenRequest).then((response: AuthenticationResult) => {
          // setting accesstoken in axios With headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.accessToken}`;
            // Call your API with the access token and return the data you need to save in state
            getData();
            
        }).catch(async (e) => {
            // Catch interaction_required errors and call interactive method to resolve
            if (e instanceof InteractionRequiredAuthError) {
                await instance.acquireTokenRedirect(tokenRequest);
            }

            throw e;
        });
    } else if (!loading && inProgress === InteractionStatus.None && !accounts.length ) {
      const tokenRequest = {
        scopes: loginRequest.scopes,
      };
        instance.acquireTokenRedirect(tokenRequest);
    }
}, [inProgress, accounts, instance, loading, getData, menuItems, menuItemRefresh, viewAs.refreshMenu]);

  useEffect(() => {
    if (window.location.pathname === '/') {
    window.location.assign(BASE_NAME);
  } else if (!window.location.pathname.toLowerCase().startsWith(BASE_NAME)) {
    window.location.assign(BASE_NAME + window.location.pathname + window.location.search);
  }
  }, []);

  return loading || inProgress === InteractionStatus.Login || menuItems.length == 0 ? (
    <ILendingLoader />
  ) : (
    <>
      {openAlert && (
        <CustomAlert
          alertText={openAlert}
          setOpenAlert={setOpenAlert}
        />
      )}
      <App router={generateBrowserRouter(menuItems)} />
    </>
  );
};
msalInstance.initialize().then(async () => {

  const rootElement = document.getElementById('root') as HTMLElement;
  const root = createRoot(rootElement);
  
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  root.render(<Main pca={msalInstance} />);
});